import { useIsAuthenticated, useMsal } from "@azure/msal-react"
import { useApi } from "auth/useApi3"
import axios from "axios"
import { apiEndpoints } from "generated/apiEndpoints"
import i18next from "i18next"
import { createContext, useEffect, useState } from "react"
import { matchPath, useHistory, useLocation } from "react-router-dom"
import { msalInstance } from "../index"

export const AppContext = createContext()

const defaultAppUser = {
  app: {
    normalPages: [],
    sysadmPages: [],
    routes: [
      {
        unauthenticatedTemplate: true,
        path: "/external/workorder/:guid",
        componentName: "ExternalWorkorder"
      }
    ]
  },
  customer: {},
  roles: [],
  profile: {},
  userSettings: []
}
const emptyState = {
  pageSize: 25,
  pageIndex: 0,
  expanded: {},
  sortBy: [],
  filters: [],
  hiddenColumns: []
}

const getLang = (lng) => {
  switch (lng) {
    case "en-US":
      return "en"
    case "sv-SE":
      return "sv"
    case "da":
      return "da"
    case "nb":
      return "nb"
    default:
      return lng
  }
}
const AppContextProvider = ({ children }) => {
  const location = useLocation()
  const history = useHistory()
  const isAuthenticated = useIsAuthenticated()
  const { inProgress, instance } = useMsal()

  const [tableState, setTableState] = useState({
    state: emptyState,
    columns: null,
    filters: []
  })
  const [fileVersion, setFileVersion] = useState()
  const [appUserResult, appUserFuncs] = useApi()
  const [loadingAppstructure, setLoadingAppstructure] = useState(true)
  const [previousPath, setPreviousPath] = useState()
  const [navigationHistory, setNavigationHistory] = useState([])
  const [userIsAuthenticated, setUserIsAuthenticated] = useState(true)
  const [currentCompanyId, setCurrentCompanyId] = useState()
  const [isSidebarOpen, toggleSideBar] = useState("")
  const [sidebarsOpen, setSidebarsOpen] = useState({})
  const [isMainNavOpen, toggleMainNav] = useState(true)
  const [language, setLanguage] = useState(
    i18next.language ? i18next.language : window.localStorage.i18nextLng
  )
  const [sasTokenResult, getSasTokenApi] = useApi()
  const [availableLanguages, setAvailableLanguages] = useState({})

  const toggleSidebarId = (sidebarId = "Default", forceState = null) => {
    if (typeof sidebarId === "boolean") {
      forceState = sidebarId
      sidebarId = "Default"
    }
    toggleSideBar((prevOpenSidebars) => {
      const isOpen = prevOpenSidebars === sidebarId

      if (forceState === true) {
        return isOpen ? prevOpenSidebars : sidebarId
      } else if (forceState === false) {
        return ""
      } else {
        return isOpen ? "" : sidebarId
      }
    })
  }
  const toggleSidebarMulti = (sidebarId, forceState = null) => {
    setSidebarsOpen((prev) => {
      if (forceState === true) {
        return { ...prev, [sidebarId]: true }
      } else if (forceState === false) {
        return { ...prev, [sidebarId]: false }
      } else {
        return { ...prev, [sidebarId]: !prev[sidebarId] }
      }
    })
  }
  useEffect(() => {
    const handleLanguageChanged = () => {
      setLanguage(i18next.language ? i18next.language : window.localStorage.i18nextLng)
    }

    i18next.on("languageChanged", handleLanguageChanged)

    return () => {
      i18next.off("languageChanged", handleLanguageChanged)
    }
  }, [i18next.language])

  useEffect(() => {
    const handleResize = () => {
      toggleMainNav(window.innerWidth >= 768)
    }

    window.addEventListener("resize", handleResize)

    // Initial check
    handleResize()

    return () => {
      window.removeEventListener("resize", handleResize)
    }
  }, [])

  // TODO: loadingFailed
  useEffect(() => {
    const loadStuff = async () => {
      setLoadingAppstructure(true)

      if (inProgress === "none" && isAuthenticated) {
        await appUserFuncs.get(apiEndpoints.app.getappuser)
      }

      try {
        var result = await axios.get("/version.txt")
        setFileVersion(result.data)
      } catch {
        setLoadingAppstructure(false)
      }
    }
    loadStuff()
  }, [inProgress, isAuthenticated, appUserFuncs])

  useEffect(() => {
    if (appUserResult.status === 2) {
      setCurrentCompanyId(
        appUserResult.data.app.mainCompanyId
          ? appUserResult.data.app.mainCompanyId
          : appUserResult.data.customer?.companies[0]?.id
      )
      setLoadingAppstructure(false)
    } else if (appUserResult.status === 3) {
      setLoadingAppstructure(false)
    }
  }, [appUserResult.status])

  useEffect(() => {
    if (previousPath !== location.pathname) {
      toggleSideBar("")
      setPreviousPath(location.pathname)
      setNavigationHistory((prevHistory) => [...prevHistory, { path: location.pathname }])
    }
  }, [location.pathname, previousPath])

  useEffect(() => {
    const currentRoute = appUserResult?.data?.app.routes.find((route) =>
      matchPath(location.pathname, { path: route.matchPath, exact: true, strict: false })
    )

    const previousRoute = appUserResult?.data?.app.routes.find((route) =>
      matchPath(previousPath, { path: route.matchPath, exact: true, strict: false })
    )

    if (!currentRoute?.matchPath.includes(":id") && !previousRoute?.matchPath.includes(":id")) {
      setTableState((prevState) => {
        return {
          ...prevState,
          paginationIndex: 0
        }
      })
    }
  }, [location])

  // ett sätt att redirecta till login automatiskt
  useEffect(() => {
    const match = matchPath(location.pathname, {
      path: "/external/workorder/:guid",
      exact: true,
      strict: false
    })

    if (
      inProgress === "none" &&
      !isAuthenticated &&
      !location.pathname.includes("/loggedout") &&
      match === null
    ) {
      instance.loginRedirect()
    }
  }, [inProgress, isAuthenticated, instance, location])

  useEffect(() => {
    async function fetchData() {
      try {
        const account = msalInstance.getActiveAccount()
        if (!account) {
          throw Error(
            "No active account! Verify a user has been signed in and setActiveAccount has been called."
          )
        }
      } catch {
        setUserIsAuthenticated(false)
      }
    }
    if (isAuthenticated) {
      fetchData()
    }
  }, [history.location.pathname, isAuthenticated])

  const signOutUser = () => {
    setUserIsAuthenticated(false)
    instance.logoutRedirect()
  }

  useEffect(() => {
    if (isAuthenticated) {
      getSasTokenApi.get(apiEndpoints.app.getlanguagesaskey)
    }
  }, [getSasTokenApi, isAuthenticated])

  useEffect(() => {
    const fetchLanguages = async () => {
      try {
        const response = await fetch(`https://api.locize.app/languages/b86c695c-62be-4454-bd14-e4b2f05a1dbd`)
        const data = await response.json()
        if (!data || Object.keys(data).length === 0) {
          throw new Error("Locize returned empty language list")
        }
        setAvailableLanguages(data)
      } catch (error) {
        try {
          if (sasTokenResult.status === 2) {
            //change this url depending on what environment you are in
            // DEV: const fallbackUrl = `https://clpdev.blob.core.windows.net/translations/languages.json?${sasTokenResult.data}`
            const fallbackUrl = `https://clpcompanyportalprod.blob.core.windows.net/translations/languages.json?${sasTokenResult.data}`
            const fallbackResponse = await fetch(fallbackUrl)
            const fallbackData = await fallbackResponse.json()
            setAvailableLanguages(fallbackData)
          }
        } catch (fallbackError) {
          console.error(fallbackError)
        }
      }
    }

    fetchLanguages()
  }, [sasTokenResult])

  return isAuthenticated ? (
    <AppContext.Provider
      value={{
        availableLanguages: availableLanguages,
        translationsSasToken: sasTokenResult.status === 2 && sasTokenResult.data,
        isAuth: userIsAuthenticated,
        appUser: appUserResult.status === 2 ? appUserResult.data : defaultAppUser,
        fileVersion: fileVersion,
        tableState: tableState,
        setTableState: setTableState,
        loadingAppstructure: loadingAppstructure,
        toggleSideBar: toggleSidebarId,
        isSidebarOpen: isSidebarOpen,
        toggleSideBarMulti: toggleSidebarMulti,
        sidebarsOpen: sidebarsOpen,
        selectedLanguage: getLang(language),
        navigationHistory: navigationHistory,
        signOutUser: () => signOutUser(),
        currentCompanyId: currentCompanyId,
        setCurrentCompanyId: (value) => setCurrentCompanyId(value),
        isMainNavOpen: isMainNavOpen,
        toggleMainNav: () => toggleMainNav(!isMainNavOpen)
      }}>
      <>{children}</>
    </AppContext.Provider>
  ) : (
    <AppContext.Provider
      value={{
        isAuth: false,
        appUser: defaultAppUser,
        loadingAppstructure: loadingAppstructure,
        selectedLanguage: getLang(language)
      }}>
      <>{children}</>
    </AppContext.Provider>
  )
}
export default AppContextProvider
